import { LocalDateTime } from 'js-joda';
import {
  SeatingTemplateTable,
  getConditionalRangeTemplateForSTTTicketTypeConfig,
  getSeatingTemplateRangeSet,
  rangeSetFromRestrictions,
} from 'state/templateInfo';
import { StoreType } from '@tock/shared/utils/connect';
import { createSelector } from 'reselect';
import { dashboard } from 'types';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import {
  getFloorplanTableCombinationsFromTemplate,
  selectedDynamicOptimizationTimeSelector,
} from 'selectors/templateInfoSelector';
import { getHasEmptyBookableRanges } from 'utils/inventory/dynamicOptimizationUtils';
import {
  getServiceTimeRangeForScheduledTemplate,
  getTicketTypesByTableElementAndTime,
} from 'selectors/routes/table-based-templates/seatingTemplateTableSelector';
import RangeSet from 'utils/range/RangeSet';

export const inheritedExperiencesOnSeatingTemplateTableCombinationSelector = createSelector(
  (state: StoreType) => state.templateInfo.template,
  (state: StoreType) => state.templateInfo.templateTables,
  (state: StoreType) => state.templateInfo.templateTableCombinations,
  getFloorplanTableCombinationsFromTemplate,
  getInheritedExperienceInfoOnSeatingTemplateTableCombination
);

export function getInheritedExperienceInfoOnSeatingTemplateTableCombination(
  { startTime: serviceStartTime, endTime: serviceEndTime }: dashboard.SeatingTemplate,
  templateTables: SeatingTemplateTable[],
  templateTableCombos: dashboard.SeatingTemplateTableCombination[],
  floorplanTableCombinations: dashboard.TableCombination[]
): Map<number, Map<number, RangeSet<LocalDateTime>>> {
  if (!floorplanTableCombinations) {
    return new Map();
  }

  const tableIdsByTableCombinationId = new Map(
    floorplanTableCombinations.map((tableCombination) => [
      tableCombination.id,
      tableCombination.tableId,
    ])
  );
  const templateTablesByTableId = new Map(templateTables.map((table) => [table.tableId, table]));

  const inheritedExperienceRangesWithTemplateTableComboId = templateTableCombos
    .map((combo) => {
      const constituentTableIds = tableIdsByTableCombinationId.get(combo.tableCombinationId) ?? [];

      const constituentTemplateTables = constituentTableIds
        .map((tableId) => templateTablesByTableId.get(tableId))
        .filter(filterNilValues);

      const intersectedRangeSetsByTicketTypeId = getInheritedExperienceInfoOnTableCombos(
        constituentTemplateTables,
        constituentTableIds,
        serviceStartTime,
        serviceEndTime
      );

      return {
        tableComboId: combo.tableCombinationId,
        intersectedRangeSetsByTicketTypeId,
      };
    })
    .filter(filterNilValues);

  const inheritedExperienceRangesByTemplateTableComboId = new Map();
  inheritedExperienceRangesWithTemplateTableComboId.forEach(
    ({ tableComboId, intersectedRangeSetsByTicketTypeId }) =>
      inheritedExperienceRangesByTemplateTableComboId.set(
        tableComboId,
        intersectedRangeSetsByTicketTypeId
      )
  );
  return inheritedExperienceRangesByTemplateTableComboId;
}

export const inheritedTicketTypeIdsByTableCombinationAndTimeSelector = createSelector(
  inheritedExperiencesOnSeatingTemplateTableCombinationSelector,
  getTicketTypesByTableCombinationAndTime
);

// Note: This returns ticket type ids that are directly applied to seating template table combos,
// and not ids that are indirectly applied through the intersection of the underlying seating template tables.
export const directlyAppliedTicketTypeIdsByTableCombinationAndTimeSelector = createSelector(
  (state: StoreType) => state.templateInfo.templateTableCombinations,
  (state: StoreType) => state.templateInfo.template,
  selectedDynamicOptimizationTimeSelector,
  (seatingTemplateTablesCombinations, seatingTemplate, selectedDynamicOptimizationTimeMinutes) => {
    const serviceTimeRange = getServiceTimeRangeForScheduledTemplate(seatingTemplate);
    return getTicketTypesByTableElementAndTime(
      seatingTemplateTablesCombinations,
      serviceTimeRange,
      selectedDynamicOptimizationTimeMinutes
    );
  }
);

export const experiencesByTableCombinationAndTimeSelector = createSelector(
  inheritedTicketTypeIdsByTableCombinationAndTimeSelector,
  directlyAppliedTicketTypeIdsByTableCombinationAndTimeSelector,
  (state: StoreType) => state.templateInfo.templateTableCombinations,
  getExperiencesByTableCombinationAndTime
);

export function getInheritedExperienceInfoOnTableCombos(
  constituentTemplateTables: dashboard.ISeatingTemplateTable[],
  constituentTableIds: number[],
  serviceStartTime: string,
  serviceEndTime: string
): Map<number, RangeSet<LocalDateTime>> {
  const numConstituentTables = constituentTemplateTables.length;

  if (numConstituentTables < constituentTableIds.length) {
    return new Map();
  }

  const allTicketTypeIdsOnTables = new Set(
    constituentTemplateTables.flatMap((templateTable) =>
      (templateTable.ticketTypeConfig ?? [])
        .map((ticketTypeConfig) => ticketTypeConfig.ticketTypeId)
        .filter(filterNilValues)
    )
  );

  const intersectedRangeSetsWithTicketTypeId = Array.from(allTicketTypeIdsOnTables)
    .map((ticketTypeId) => {
      const conditionalRangesForTicketTypeOnTables = constituentTemplateTables
        .map((table) =>
          table.ticketTypeConfig?.find((config) => config.ticketTypeId === ticketTypeId)
        )
        .filter(filterNilValues)
        .map((config) => getConditionalRangeTemplateForSTTTicketTypeConfig(config).templateRange) // TODO: Future dynamic optimization work
        .filter(filterNilValues)
        .filter(
          (conditionalRange) =>
            !getHasEmptyBookableRanges(conditionalRange.bookableRanges?.bookableRange)
        );

      if (conditionalRangesForTicketTypeOnTables.length < numConstituentTables) {
        return { ticketTypeId, intersectedRangeSets: RangeSet.empty() };
      }

      const allRangeSets = conditionalRangesForTicketTypeOnTables.map((conditionalRange) => {
        const isFullServiceApplication = conditionalRange?.bookableRanges === undefined;
        if (isFullServiceApplication) {
          return getSeatingTemplateRangeSet(serviceStartTime, serviceEndTime);
        }
        return rangeSetFromRestrictions(
          conditionalRange?.bookableRanges?.bookableRange ?? [],
          ({ startTimeInclusive }) => startTimeInclusive,
          ({ endTimeExclusive }) => endTimeExclusive
        );
      });

      const intersectedRangeSets = allRangeSets.reduce((acc, rangeSet) =>
        acc.intersectRangeSet(rangeSet)
      );
      return { ticketTypeId, intersectedRangeSets };
    })
    .filter(filterNilValues);

  const intersectedRangeSetsByTicketTypeId = new Map();
  intersectedRangeSetsWithTicketTypeId.forEach(({ ticketTypeId, intersectedRangeSets }) => {
    if (intersectedRangeSets.ranges().length > 0) {
      intersectedRangeSetsByTicketTypeId.set(ticketTypeId, intersectedRangeSets);
    }
  });
  return intersectedRangeSetsByTicketTypeId;
}

// Exported for testing
export function getTicketTypesByTableCombinationAndTime(
  experienceBookableRangesByTableAndTicketType: Map<number, Map<number, RangeSet<LocalDateTime>>>
): Map<number, Map<string, number[]>> {
  const ticketTypesByTableCombinationAndTime = new Map<number, Map<string, number[]>>();

  for (const [tableId, ticketMap] of experienceBookableRangesByTableAndTicketType) {
    const tableMap = new Map<string, number[]>();
    for (const [ticketId, rangeSet] of ticketMap) {
      for (const range of rangeSet.ranges()) {
        let current = range.low;
        while (current.isBefore(range.high)) {
          const timeKey = current.toLocalTime().toString();
          if (!tableMap.has(timeKey)) {
            tableMap.set(timeKey, [ticketId]);
          } else {
            const ticketIds = tableMap.get(timeKey);
            ticketIds?.push(ticketId);
          }
          current = current.plusMinutes(15);
        }
      }
    }

    ticketTypesByTableCombinationAndTime.set(tableId, tableMap);
  }

  return ticketTypesByTableCombinationAndTime;
}

function getExperiencesByTableCombinationAndTime(
  inheritedExperiences: Map<number, Map<string, number[]>>,
  directlyAppliedExperiences: Map<number, Map<string, number[]>>,
  templateTableCombos: dashboard.SeatingTemplateTableCombination[]
): Map<number, Map<string, number[]>> {
  const returnMap = new Map<number, Map<string, number[]>>();
  templateTableCombos.forEach(({ tableCombinationId, ticketTypeConfig }) => {
    const effectiveTableMap =
      (ticketTypeConfig ?? []).length > 0
        ? directlyAppliedExperiences.get(tableCombinationId)
        : inheritedExperiences.get(tableCombinationId);

    if (effectiveTableMap) {
      returnMap.set(tableCombinationId, effectiveTableMap);
    }
  });
  return returnMap;
}
